/**
 * MobileShell — Layout wrapper for the Food Pulse mobile web app.
 *
 * Provides the branded header (logo + home + notification + settings)
 * and the fixed bottom navigation (News/Events · My Lists · My Deals ·
 * Recipes · Groups). Content renders via <Outlet />.
 *
 * The onboarding + splash screens run their own layout (no header /
 * no bottom nav) — those routes use ``MobileFullScreen`` instead.
 */
import { Outlet, useNavigate, useLocation, Link } from "react-router-dom";
import { Bell, Settings as SettingsIcon, Home as HomeIcon, ArrowLeft, Megaphone, List, Tag, UtensilsCrossed, Users } from "lucide-react";
import { MobileLanguagePickerButton } from "./MobileLanguagePicker";

const FP_PURPLE = "#653091";
const FP_GREEN_TOP = "#A9D64C";
const FP_GREEN_MID = "#B5DA5E";

// Real Food Pulse round logo (served from /public/foodpulse/).
const FP_LOGO = "/foodpulse/fp-logo-round.jpg";

const NAV_ITEMS = [
  { key: "news",    to: "/m/news",    label: "News/Events",  Icon: Megaphone },
  { key: "lists",   to: "/m/lists",   label: "My Lists",     Icon: List },
  { key: "deals",   to: "/m/my-deals",label: "My Deals",     Icon: Tag },
  { key: "recipes", to: "/m/recipes", label: "Recipes",      Icon: UtensilsCrossed },
  { key: "groups",  to: "/m/groups",  label: "Groups",       Icon: Users },
];

export function MobileHeader({ showBack = false, showHome = true }) {
  const nav = useNavigate();
  return (
    <header
      className="sticky top-0 z-30 flex items-center justify-between px-4 py-3"
      style={{
        background: `linear-gradient(180deg, ${FP_GREEN_TOP} 0%, ${FP_GREEN_MID} 100%)`,
      }}
      data-testid="mobile-header"
    >
      <div className="flex items-center gap-2 min-w-0">
        {showBack && (
          <button
            onClick={() => nav(-1)}
            aria-label="Back"
            data-testid="mobile-back"
            className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
        )}
        <Link
          to="/m"
          className="flex items-center gap-2 min-w-0"
          data-testid="mobile-logo-home"
          aria-label="Food Pulse mobile home"
        >
          <img
            src={FP_LOGO}
            alt="Food Pulse"
            className="w-9 h-9 rounded-full object-cover shadow-sm ring-2 ring-white"
          />
          <span
            className="font-serif font-bold text-[15px] leading-none tracking-tight truncate"
            style={{ color: FP_PURPLE }}
          >
            Food Pulse<sup className="text-[8px] font-sans ml-0.5 align-super">™</sup>
          </span>
        </Link>
        {showHome && (
          <a
            href="/food-pulse"
            data-testid="mobile-home-icon"
            aria-label="Open Food Pulse full site"
            title="Open full site"
            className="w-9 h-9 ml-1 rounded-full flex items-center justify-center hover:bg-white/25"
          >
            <HomeIcon className="w-5 h-5" style={{ color: "#fff" }} />
          </a>
        )}
      </div>
      <div className="flex items-center gap-1.5 flex-shrink-0">
        <MobileLanguagePickerButton />
        <button aria-label="Notifications" data-testid="mobile-notifications" className="w-9 h-9 rounded-full hover:bg-white/20 flex items-center justify-center">
          <Bell className="w-5 h-5" />
        </button>
        <Link to="/m/settings" data-testid="mobile-settings" aria-label="Settings" className="w-9 h-9 rounded-full hover:bg-white/20 flex items-center justify-center">
          <SettingsIcon className="w-5 h-5" />
        </Link>
      </div>
    </header>
  );
}

export function MobileBottomNav() {
  const { pathname } = useLocation();
  return (
    <nav
      className="fixed bottom-0 left-0 right-0 z-30 flex items-center justify-around px-2 py-2"
      style={{ background: FP_PURPLE, color: "#fff" }}
      data-testid="mobile-bottom-nav"
    >
      {NAV_ITEMS.map(({ key, to, label, Icon }) => {
        const active = pathname === to || pathname.startsWith(to + "/");
        return (
          <Link
            key={key}
            to={to}
            data-testid={`mobile-nav-${key}`}
            className="flex-1 flex flex-col items-center justify-center gap-0.5 py-1 rounded-lg"
            style={{ opacity: active ? 1 : 0.85, background: active ? "rgba(255,255,255,0.15)" : "transparent" }}
          >
            <Icon className="w-5 h-5" />
            <span className="text-[10px] font-medium leading-tight text-center">{label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

export default function MobileShell() {
  return (
    <div className="min-h-screen flex flex-col" style={{ background: "#FBFDF3" }} data-testid="mobile-shell">
      <MobileHeader />
      <main className="flex-1 pb-20">
        <Outlet />
      </main>
      <MobileBottomNav />
    </div>
  );
}
